import { useMemo, useCallback, useRef, type Dispatch, type SetStateAction, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { I18N_KEYS } from '@/language/keys';
import { useAnalytics } from '@/hooks/useAnalytics';
import { PROMPT_VARIABLE_EVENT_NAMES } from '@/consts/analytics.consts';
import { MentionMenuContent } from '../MentionMenuContent';
import { INITIAL_CONTEXT_MENU_STATE } from '../constants';
import { getTagDisplay } from '../utils';
import { insertTagAtMention, removeMentionTrigger } from '../tiptapEditorUtils';
import { useEditorActions } from './useEditorActions';
import { useMentionMenuKeyboard } from './useMentionMenuKeyboard';
import type { AvailableInput, PromptEditorContextMenuState } from '../types';
import type { Editor } from '@tiptap/react';
import type { PromptDisplayMode } from 'web';
import type { MenuAction } from '@/components/Menu/Actions';

interface UsePromptEditorMenuItemsProps {
  editor: Editor;
  availableInputs: AvailableInput[];
  displayMode: PromptDisplayMode;
  contextMenu: PromptEditorContextMenuState;
  setContextMenu: Dispatch<SetStateAction<PromptEditorContextMenuState>>;
  addInputHandle: () => AvailableInput;
  onCreatePrompt: (queryText: string) => AvailableInput | undefined;
}

export const usePromptEditorMenuItems = ({
  editor,
  availableInputs,
  displayMode,
  contextMenu,
  setContextMenu,
  addInputHandle,
  onCreatePrompt,
}: UsePromptEditorMenuItemsProps) => {
  const { t } = useTranslation();
  const { track } = useAnalytics();
  const mentionMenuFooterRef = useRef<HTMLLIElement>(null);
  const mentionMenuItemsRef = useRef<Map<string, HTMLLIElement>>(new Map());
  const mentionQueryText = contextMenu.type === 'mention' ? contextMenu.queryText : '';

  const { handleMentionMenuKeyboard } = useMentionMenuKeyboard({
    editor,
    contextMenu,
    handleSetContextMenu: setContextMenu,
    displayMode,
    onCreatePrompt,
  });

  const { insertInputTag, handleTagCopy, handleTagDuplicate, handleTagDelete, closeContextMenu } = useEditorActions({
    editor,
    availableInputs,
    displayMode,
    contextMenu,
    setContextMenu,
  });

  const onAddVariableClick = useCallback(() => {
    if (!editor) return;

    // Create new input handle and get the key
    const inputHandle = addInputHandle();

    if (inputHandle.key) {
      insertTagAtMention({
        editor,
        tagKey: inputHandle.key,
        ...getTagDisplay(inputHandle, displayMode),
      });
    }

    track(PROMPT_VARIABLE_EVENT_NAMES.ADD_VARIABLE, { source: 'menu' });
    closeContextMenu();
  }, [addInputHandle, closeContextMenu, editor, displayMode, track]);

  // Create a prompt from the mention queryText (same as pressing Enter in the editor)
  const onCreatePromptClick = useCallback(() => {
    const trimmedQueryText = mentionQueryText.trim();
    if (!trimmedQueryText || !editor) return;

    // Create the prompt node and get the new tag key
    const inputHandle = onCreatePrompt(trimmedQueryText);

    if (inputHandle) {
      insertTagAtMention({
        editor,
        tagKey: inputHandle.key,
        ...getTagDisplay({ ...inputHandle, value: trimmedQueryText }, displayMode),
      });
    } else {
      removeMentionTrigger(editor, true);
    }

    setContextMenu(INITIAL_CONTEXT_MENU_STATE);
  }, [onCreatePrompt, mentionQueryText, setContextMenu, editor, displayMode]);

  /**
   * Handles arrow key navigation between menu items and footer in the mention menu.
   * Manages focus transitions between scroll area items and the footer item.
   */
  const handleMenuFooterNavigation = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    // Only handle ArrowDown/ArrowUp on list items
    if ((event.key !== 'ArrowDown' && event.key !== 'ArrowUp') || target.tagName !== 'LI') return;

    const scrollItems = Array.from(mentionMenuItemsRef.current.values());
    const isFooter = target === mentionMenuFooterRef.current;
    const currentIndex = isFooter ? -1 : scrollItems.indexOf(target as HTMLLIElement);

    if (isFooter) {
      // On the footer: ArrowDown → first scroll item, ArrowUp → last scroll item
      if (scrollItems.length === 0) return;
      event.preventDefault();
      event.stopPropagation();
      const targetItem = event.key === 'ArrowDown' ? scrollItems[0] : scrollItems[scrollItems.length - 1];
      (targetItem as HTMLElement).focus();
    } else if (currentIndex !== -1) {
      const isFirstItem = currentIndex === 0;
      const isLastItem = currentIndex === scrollItems.length - 1;
      // At scroll area boundaries, focus the footer item
      if ((event.key === 'ArrowDown' && isLastItem) || (event.key === 'ArrowUp' && isFirstItem)) {
        event.preventDefault();
        event.stopPropagation();
        mentionMenuFooterRef.current?.focus();
      }
    }
  }, []);

  // Handles keyboard events on the mention menu:
  // - ArrowDown/ArrowUp: navigate between scroll area items and footer
  // - Enter, Typing, Backspace, Escape: handled by shared handleMentionMenuKeyboard
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (contextMenu.type !== 'mention' || !editor) return;

      // Call the shared keyboard handler for Enter, typing, backspace, escape
      const handled = handleMentionMenuKeyboard(e.nativeEvent);

      if (handled) return;

      // Handle arrow key navigation between menu items and footer
      handleMenuFooterNavigation(e);
    },
    [contextMenu.type, editor, handleMentionMenuKeyboard, handleMenuFooterNavigation],
  );

  // Menu items for tag context menu
  const tagMenuItems: MenuAction[] = useMemo(
    () => [
      {
        name: t(I18N_KEYS.GENERAL.COPY),
        action: handleTagCopy,
        shortcutKey: 'C',
      },
      {
        name: t(I18N_KEYS.RECIPE_MAIN.NODES.PROMPT_NODE_V3.TAG_CONTEXT_MENU.DUPLICATE),
        action: handleTagDuplicate,
        shortcutKey: 'D',
      },
      {
        name: t(I18N_KEYS.RECIPE_MAIN.NODES.PROMPT_NODE_V3.TAG_CONTEXT_MENU.DELETE),
        action: handleTagDelete,
        shortcut: '⌫',
      },
    ],
    [t, handleTagCopy, handleTagDuplicate, handleTagDelete],
  );

  const mentionChildren: ReactNode = contextMenu.type === 'mention' && (
    <MentionMenuContent
      availableInputs={availableInputs}
      footerRef={mentionMenuFooterRef}
      menuItemsRef={mentionMenuItemsRef}
      onInsertInputTag={insertInputTag}
      onAddVariableClick={onAddVariableClick}
      queryText={mentionQueryText}
      onCreatePromptClick={onCreatePromptClick}
    />
  );

  return {
    isOpen: contextMenu.type !== 'closed',
    menuItems: contextMenu.type === 'tag' ? tagMenuItems : undefined,
    children: mentionChildren,
    handleKeyDown,
    closeContextMenu,
  };
};
